import { extractTopLevelDomain } from './Url.ts';

/**
 * The static brand map. `domain` is the brand's live shop domain, without subdomain.
 *
 * Several brands share one domain, either because they are editions of the same title
 * (Libelle Specials, Donald Duck Pocket) or because the title was merged into another
 * brand (Ariadne at Home into Libelle, Stijlvol Wonen into vtwonen). For every shared
 * domain exactly one entry carries `isDomainOwner: true`: that is the canonical brand
 * a host resolves to. Without it, host resolution would silently depend on the order
 * the entries happen to be declared in.
 *
 * @see getBrandCodeFromHost
 */
export const brands = {
  FLNL: {
    domain: 'fluister.nl',
    code: 'FLNL',
    fullName: 'Fluister',
  },
  FLBE: {
    domain: 'fluister.be',
    code: 'FLBE',
    fullName: 'Fluister',
  },
  AD: {
    domain: 'ad.nl',
    code: 'AD',
    fullName: 'AD',
  },
  ARI: {
    domain: 'libelle.nl',
    code: 'ARI',
    fullName: 'Ariadne at Home',
  },
  AUT: {
    domain: 'autoweek.nl',
    isDomainOwner: true,
    code: 'AUT',
    fullName: 'AutoWeek',
  },
  ATR: {
    domain: 'autoweek.nl',
    code: 'ATR',
    fullName: 'AutoWeek Classics',
  },
  BD: {
    domain: 'bd.nl',
    code: 'BD',
    fullName: 'Brabants Dagblad',
  },
  BFWO: {
    domain: 'vtwonen.be',
    code: 'BFWO',
    fullName: 'Feeling Wonen',
  },
  BS: {
    domain: 'bndestem.nl',
    code: 'BS',
    fullName: 'BN DeStem',
  },
  BSVW: {
    domain: 'vtwonen.be',
    code: 'BSVW',
    fullName: 'vtwonen Design',
  },
  BVWO: {
    domain: 'vtwonen.be',
    isDomainOwner: true,
    code: 'BVWO',
    fullName: 'vtwonen',
  },
  BWOL: {
    domain: 'vtwonen.be',
    code: 'BWOL',
    fullName: 'Wonen Landelijke Stijl',
  },
  BDON: {
    domain: '',
    code: 'BDON',
    fullName: 'Donald Duck',
  },
  BFLN: {
    domain: '',
    code: 'BFLN',
    fullName: 'Flow',
  },
  BDAN: {
    domain: '',
    code: 'BDAN',
    fullName: 'Zo Zit Dat',
  },
  DA: {
    domain: 'dagallemaal.be',
    code: 'DA',
    fullName: 'Dag Allemaal',
  },
  DAT: {
    domain: 'zozitdat.nl',
    code: 'DAT',
    fullName: 'Zo Zit Dat',
  },
  DON: {
    domain: 'donaldduck.nl',
    isDomainOwner: true,
    code: 'DON',
    fullName: 'Donald Duck',
  },
  DDJ: {
    domain: 'donaldduck.nl',
    code: 'DDJ',
    fullName: 'Donald Duck Junior',
  },
  DG: {
    domain: 'gelderlander.nl',
    code: 'DG',
    fullName: 'De Gelderlander',
  },
  DM: {
    domain: 'demorgen.be',
    code: 'DM',
    fullName: 'De Morgen',
  },
  DONEX: {
    domain: 'donaldduck.nl',
    code: 'DONEX',
    fullName: 'Donald Duck Extra',
  },
  DONPO: {
    domain: 'donaldduck.nl',
    code: 'DONPO',
    fullName: 'Donald Duck Pocket',
  },
  ED: {
    domain: 'ed.nl',
    code: 'ED',
    fullName: 'Eindhovens Dagblad',
  },
  EHI: {
    domain: 'vtwonen.nl',
    code: 'EHI',
    fullName: 'Eigen Huis & Interieur',
  },
  FLR: {
    domain: 'flair.nl',
    allDomains: ['flaironline.nl', 'flair.nl'],
    code: 'FLR',
    fullName: 'Flair',
  },
  GG: {
    domain: 'goedgevoel.be',
    code: 'GG',
    fullName: 'Goed Gevoel',
  },
  HL: {
    domain: 'hln.be',
    code: 'HL',
    fullName: 'HLN',
  },
  HP: {
    domain: 'parool.nl',
    code: 'HP',
    fullName: 'Het Parool',
  },
  HU: {
    domain: 'humo.be',
    code: 'HU',
    fullName: 'HUMO',
  },
  KATRI: {
    domain: 'donaldduck.nl',
    code: 'KATRI',
    fullName: 'Katrien Duck',
  },
  KWE: {
    domain: 'kidsweek.nl',
    code: 'KWE',
    fullName: 'Kidsweek',
  },
  LIB: {
    domain: 'libelle.nl',
    isDomainOwner: true,
    code: 'LIB',
    fullName: 'Libelle',
  },
  LIBBO: {
    domain: 'libelle.nl',
    code: 'LIBBO',
    fullName: 'Libelle Bookazine',
  },
  LIBPZ: {
    domain: 'libelle.nl',
    code: 'LIBPZ',
    fullName: 'Libelle Puzzelen',
  },
  LIBSP: {
    domain: 'libelle.nl',
    code: 'LIBSP',
    fullName: 'Libelle Specials',
  },
  MAR: {
    domain: 'margriet.nl',
    isDomainOwner: true,
    code: 'MAR',
    fullName: 'Margriet',
  },
  MARPZ: {
    domain: 'margriet.nl',
    code: 'MARPZ',
    fullName: 'Margriet Puzzelen',
  },
  MARSP: {
    domain: 'margriet.nl',
    code: 'MARSP',
    fullName: 'Margriet Specials',
  },
  OVN: {
    domain: 'oudersvannu.nl',
    code: 'OVN',
    fullName: 'Ouders van Nu',
  },
  PAK12: {
    domain: 'lezerij.nl',
    code: 'PAK12',
    fullName: 'Lezerij',
  },
  PRIMO: {
    domain: 'primo.be',
    code: 'PRIMO',
    fullName: 'Primo',
  },
  PZ: {
    domain: 'pzc.nl',
    code: 'PZ',
    fullName: 'PZC',
  },
  ST: {
    domain: 'destentor.nl',
    code: 'ST',
    fullName: 'De Stentor',
  },
  STO: {
    domain: 'story.nl',
    code: 'STO',
    fullName: 'Story',
  },
  STORYBE: {
    domain: 'story.be',
    code: 'STORYBE',
    fullName: 'Story',
  },
  SVW: {
    domain: 'vtwonen.nl',
    code: 'SVW',
    fullName: 'vtwonen Design',
  },
  TR: {
    domain: 'trouw.nl',
    code: 'TR',
    fullName: 'Trouw',
  },
  TSBE: {
    domain: 'lezerij.be',
    code: 'TSBE',
    fullName: 'Lezerij',
  },
  TT: {
    domain: 'tubantia.nl',
    code: 'TT',
    fullName: 'Tubantia',
  },
  TTA: {
    domain: 'totaaltv.nl',
    code: 'TTA',
    fullName: 'Totaal TV',
  },
  TVBLAD: {
    domain: 'teveblad.be',
    code: 'TVBLAD',
    fullName: 'TeVe Blad',
  },
  TVFAM: {
    domain: 'tvfamilie.be',
    code: 'TVFAM',
    fullName: 'TV Familie',
  },
  VK: {
    domain: 'volkskrant.nl',
    code: 'VK',
    fullName: 'De Volkskrant',
  },
  VRO: {
    domain: 'veronicasuperguide.nl',
    allDomains: ['superguide.nl', 'veronicasuperguide.nl', 'veronicamagazine.nl'],
    code: 'VRO',
    fullName: 'Veronica Superguide',
  },
  VTW: {
    domain: 'vtwonen.nl',
    isDomainOwner: true,
    code: 'VTW',
    fullName: 'vtwonen',
  },
  WLS: {
    domain: 'vtwonen.nl',
    code: 'WLS',
    fullName: 'Wonen Landelijke Stijl',
  },
  KIOSK: {
    domain: 'kiosk.nl',
    code: 'KIOSK',
    fullName: 'Kiosk',
  },
  KIOSKBE: {
    domain: 'kiosk.be',
    code: 'KIOSKBE',
    fullName: 'Kiosk',
  },
  TOPICS: {
    domain: 'topics.nl',
    code: 'TOPICS',
    fullName: 'Topics',
  },
  TOPICSBE: {
    domain: 'topics.be',
    code: 'TOPICSBE',
    fullName: 'Topics',
  },
  LINDA: {
    domain: 'linda.nl',
    code: 'LINDA',
    fullName: 'Linda',
  },
} as const;

export function getBrandCode({ brandCode, hostname }: { brandCode?: string; hostname: string }) {
  const brand = brandCode?.toLowerCase() || getBrandCodeFromHost(hostname);
  return brand;
}

/**
 * Resolves a hostname to its brand code.
 *
 * Several brands share a single shop domain, so a host can match more than one entry.
 * The entry flagged with `isDomainOwner` is the canonical brand for that domain and
 * always wins, which keeps resolution independent of the order the brands are declared
 * in. Entries with an empty domain never match.
 *
 * @param host The hostname to resolve, already reduced to its top-level domain.
 * @returns The lowercased brand code, or `undefined` when no brand claims the host.
 */
export function getBrandCodeFromHost(host: string) {
  if (!host) return undefined;

  const matches = Object.values(brands).filter((brand) => brand.domain && brand.domain.includes(host));
  const brand = matches.find((brand) => 'isDomainOwner' in brand) ?? matches.at(0);

  return brand?.code?.toLowerCase();
}

export function getDomain(brandCode: string) {
  const found = Object.entries(brands)
    .find(([key]) => key === brandCode?.toUpperCase())
    ?.at(1);

  if (typeof found === 'string') return undefined;

  return found;
}

export function getBrandCodeModified(host: string) {
  const domain = extractTopLevelDomain(host || '');
  const brandCode = getBrandCodeFromHost(domain || '');

  if (!brandCode) return null;

  const brandCodeModified = brandCode.toLowerCase() === 'ad' ? 'algemeen-dagblad' : brandCode.toLowerCase();

  return brandCodeModified;
}
